import React, { useCallback, useMemo } from "react";
import type { JSX } from "react";
import { useDetectClickOutside } from "react-detect-click-outside";

import { isArray } from "lodash";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import LineProcess from "@/pages/session-detail/components/line-process/line-process";

import {
    ArrowIconWrapper,
    PopUp,
    PopUpListItem,
    Toggle,
} from "@/components/filters/filters.styles";
import { ChevronDownSquareCornersIcon24, DirectionArrowTopRightIcon24 } from "@/components/icons";
import PageHeader from "@/components/ui/page-header/page-header";

import { DropdownFilterVariant } from "@/types/filters";

import {
    ComponentsScrollableWrapper,
    ModalContentWrapperForScrollableContent,
    RightContentWrapperDiv,
} from "../../modal-contents.styles";

interface TargetModalContentProps {
    scrollableContainerHeight: number;
    logoUrl: string;
    getSessionSubtitleDetails: JSX.Element;
    subDimensionsData: any;
}

//Rendered by stats-summary-with-modal-report.tsx | StatsSummary in heat-next
//Modal category "Target"
const TargetModalContent: React.FC<TargetModalContentProps> = ({
    scrollableContainerHeight,
    logoUrl,
    getSessionSubtitleDetails,
    subDimensionsData,
}) => {
    const {
        isTargetFilterOpen,
        setIsTargetFilterOpen,
        targetFilterOptions,
        selectedTarget,
        setSelectedTarget,
        selectedTargetIndex,
        setSelectedTargetIndex,
    } = useSessionDetailsContext();

    const clickOutsideRef = useDetectClickOutside({
        onTriggered: () => setIsTargetFilterOpen(false),
    });

    const hasSubDimensionsData = isArray(subDimensionsData);

    //map each subDimension to selected Dimension
    const mapTargetIdToSelectedTarget = useCallback(
        (selectedTarget: string | null) => {
            const lineProcessData = (hasSubDimensionsData ? subDimensionsData : [])?.find(
                (target: any) => {
                    return target?.id === selectedTarget;
                },
            );

            return lineProcessData?.dataset?.["line-process"];
        },
        [selectedTarget, subDimensionsData],
    );

    const selectedTargetData = useMemo(() => {
        return selectedTarget ? mapTargetIdToSelectedTarget(selectedTarget) : [];
    }, [selectedTarget, subDimensionsData]);

    //toggle filters button
    const handleFilterButtonToggle = useCallback(
        (event: any) => {
            event.stopPropagation();

            setIsTargetFilterOpen(!isTargetFilterOpen);
        },
        [isTargetFilterOpen],
    );

    //handles filter option click
    const handleFilterOptionClick = (event: any, index: number, option: any) => {
        event.stopPropagation();
        setSelectedTargetIndex(index);
        setSelectedTarget(option);
        setIsTargetFilterOpen(false); //to close filters when option is selected
    };

    return (
        <ModalContentWrapperForScrollableContent>
            <PageHeader
                title={selectedTarget || "Target"}
                logoUrl={logoUrl}
                subtitleDetailText={getSessionSubtitleDetails}
                rightContent={
                    <RightContentWrapperDiv>
                        <Toggle
                            ref={clickOutsideRef}
                            isOpen={isTargetFilterOpen}
                            onClick={handleFilterButtonToggle}
                            variant={DropdownFilterVariant.PrimaryGrey800}
                        >
                            <DirectionArrowTopRightIcon24 />
                            {selectedTarget}
                            <ArrowIconWrapper isOpen={isTargetFilterOpen}>
                                <ChevronDownSquareCornersIcon24 />
                            </ArrowIconWrapper>
                        </Toggle>

                        {isTargetFilterOpen && hasSubDimensionsData && (
                            <PopUp
                                id="modal-filters-popup-id"
                                variant={DropdownFilterVariant.PrimaryGrey800}
                                popUpWidth="16.125rem"
                            >
                                {targetFilterOptions?.map((option: any, index: number) => {
                                    return (
                                        <PopUpListItem
                                            variant={DropdownFilterVariant.PrimaryGrey800}
                                            isSelected={selectedTargetIndex === index}
                                            key={option}
                                            onClick={(event: React.MouseEvent) => {
                                                handleFilterOptionClick(event, index, option);
                                            }}
                                        >
                                            {option}
                                        </PopUpListItem>
                                    );
                                })}
                            </PopUp>
                        )}
                    </RightContentWrapperDiv>
                }
            />

            <ComponentsScrollableWrapper
                //id is needed to calculate the scrollable container's height
                id="components-scrollable-wrapper"
                scrollableContainerHeight={scrollableContainerHeight}
            >
                <LineProcess isInsideModal subDimensionsData={selectedTargetData} />
            </ComponentsScrollableWrapper>
        </ModalContentWrapperForScrollableContent>
    );
};

export default TargetModalContent;
